// ============================================================
// COMPLIANCE SERVICE - AMOE, KYC, and Compliance Orchestration
// ============================================================

import { db, auth } from './firebase';
import { API_BASE as SHARED_API_BASE } from '../utils/apiBase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  increment
} from 'firebase/firestore';
import { geoService } from './geoService';
import {
  AMOEClaim,
  AMOEEligibility,
  AMOEIneligibleReason,
  PlatformSettings,
  DEFAULT_PLATFORM_SETTINGS,
  KYCStatus,
  KYCVerification,
  ComplianceCheckResult,
  RedemptionEligibility,
  GeoCheckResult,
  AuditLog,
  AuditAction,
} from '../types/complianceTypes';

class ComplianceService {
  // ============================================================
  // PLATFORM SETTINGS
  // ============================================================

  /**
   * Get platform settings
   */
  async getPlatformSettings(): Promise<PlatformSettings> {
    try {
      const settingsRef = doc(db, 'platformSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        return settingsSnap.data() as PlatformSettings;
      }

      // Return defaults without trying to create document
      // Admin should create platformSettings/default in Firebase Console
      console.warn('[ComplianceService] platformSettings/default not found, using defaults');
      return {
        ...DEFAULT_PLATFORM_SETTINGS,
        updatedAt: new Date(),
        updatedBy: 'system',
      };
    } catch (error) {
      console.error('[ComplianceService] Error fetching settings:', error);
      return {
        ...DEFAULT_PLATFORM_SETTINGS,
        updatedAt: new Date(),
        updatedBy: 'system',
      };
    }
  }

  /**
   * Update platform settings (admin only)
   */
  async updatePlatformSettings(
    updates: Partial<PlatformSettings>, 
    adminId: string
  ): Promise<void> {
    try {
      const settingsRef = doc(db, 'platformSettings', 'default');
      await updateDoc(settingsRef, {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: adminId,
      });

      // Clear geo cache since settings may have changed
      geoService.clearCache();

      // Audit log
      await this.logAudit('SETTINGS_UPDATED', 'system', adminId, { updates });
    } catch (error) {
      console.error('[ComplianceService] Error updating settings:', error);
      throw error;
    }
  }

  // ============================================================
  // AMOE (Alternative Method of Entry)
  // ============================================================

  /**
   * Check if user is eligible for free PKCash
   */
  async checkAMOEEligibility(userId: string): Promise<AMOEEligibility> {
    try {
      const settings = await this.getPlatformSettings();
      
      // Check if AMOE is enabled
      if (!settings.amoeEnabled) {
        return {
          eligible: false,
          reason: 'ACCOUNT_SUSPENDED', // Generic - AMOE disabled
          currentPKCash: 0,
        };
      }

      // Get user's wallet
      const walletRef = doc(db, 'wallets', userId);
      const walletSnap = await getDoc(walletRef);
      const wallet = walletSnap.exists() ? walletSnap.data() : { pkCash: 0 };

      // Check if user has balance below $1 (AMOE threshold)
      //  CRITICAL: Users can claim when balance is BELOW $1, not just $0

      if (settings.amoeRequiresZeroBalance && wallet.pkCash >= 1) {
        return {
          eligible: false,
          reason: 'HAS_BALANCE',
          currentPKCash: wallet.pkCash,
        };
      }

      // Check cooldown - get last claim
      const lastClaim = await this.getLastAMOEClaim(userId);
      if (lastClaim) {
        const cooldownMs = settings.amoeCooldownHours * 60 * 60 * 1000;
        const nextEligibleAt = new Date(lastClaim.claimedAt.getTime() + cooldownMs);
        
        if (Date.now() < nextEligibleAt.getTime()) {
          return {
            eligible: false,
            reason: 'COOLDOWN_ACTIVE',
            nextEligibleAt,
            currentPKCash: wallet.pkCash,
            lastClaimAt: lastClaim.claimedAt,
          };
        }
      }

      // Check email verification (if required)
      //  CRITICAL: Check Firebase Auth directly - Firestore may not be synced!

      if (settings.amoeRequiresVerifiedEmail) {
        const currentUser = auth.currentUser;
        
        // Check Firebase Auth's emailVerified (the source of truth)
        if (!currentUser?.emailVerified) {
          return {
            eligible: false,
            reason: 'NOT_VERIFIED',
            currentPKCash: wallet.pkCash,
          };
        }
      }

      // Check age verification (if required)
      if (settings.amoeRequiresAgeVerification) {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        const user = userSnap.exists() ? userSnap.data() : null;
        
        // Check if DOB confirms 18+
        if (!user?.dateOfBirth) {
          return {
            eligible: false,
            reason: 'AGE_NOT_VERIFIED',
            currentPKCash: wallet.pkCash,
          };
        }
        
        const dob = new Date(user.dateOfBirth);
        const age = this.calculateAge(dob);
        if (age < settings.minimumAge) {
          return {
            eligible: false,
            reason: 'AGE_NOT_VERIFIED',
            currentPKCash: wallet.pkCash,
          };
        }
      }

      // Check geo restriction
      const geoCheck = await geoService.checkIP();
      if (!geoCheck.allowed) {
        return {
          eligible: false,
          reason: geoCheck.isVPN ? 'VPN_DETECTED' : 'GEO_RESTRICTED',
          currentPKCash: wallet.pkCash,
        };
      }

      // All checks passed!
      return {
        eligible: true,
        currentPKCash: wallet.pkCash,
        lastClaimAt: lastClaim?.claimedAt,
      };
    } catch (error) {
      console.error('[ComplianceService] Error checking AMOE eligibility:', error);
      return {
        eligible: false,
        reason: 'ACCOUNT_SUSPENDED',
        currentPKCash: 0,
      };
    }
  }

  /**
   * Claim free PKCash (AMOE) - Calls secure Cloud Function
   *  SECURE: All validation and writes done server-side

   */
  async claimFreePKCash(userId: string): Promise<{ success: boolean; amount?: number; error?: string }> {
    try {
      // Get auth token
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return {
          success: false,
          error: 'Please sign in to claim free PKCash',
        };
      }

      const token = await currentUser.getIdToken();
      
      // Call secure Cloud Function
      const API_BASE = SHARED_API_BASE;
      
      const response = await fetch(`${API_BASE}/claimAMOE`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Failed to claim free PKCash',
        };
      }

      // Audit log (client-side for analytics, server already logged)
      console.log(`🎁 AMOE claimed via secure endpoint: $${data.amount}`);

      return {
        success: true,
        amount: data.amount,
      };
    } catch (error) {
      console.error('[ComplianceService] Error claiming AMOE:', error);
      return {
        success: false,
        error: 'Failed to claim free PKCash. Please try again.',
      };
    }
  }

  /**
   * Get user's last AMOE claim
   */
  private async getLastAMOEClaim(userId: string): Promise<AMOEClaim | null> {
    try {
      const claimsRef = collection(db, 'amoeClaims');
      const q = query(
        claimsRef,
        where('userId', '==', userId),
        where('status', '==', 'granted'),
        orderBy('claimedAt', 'desc'),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      const data = doc.data();
      
      return {
        id: doc.id,
        ...data,
        claimedAt: data.claimedAt?.toDate?.() || new Date(data.claimedAt),
      } as AMOEClaim;
    } catch (error) {
      console.error('[ComplianceService] Error getting last AMOE claim:', error);
      return null;
    }
  }

  /**
   * Get human-readable AMOE ineligibility message
   */
  getAMOEIneligibleMessage(reason: AMOEIneligibleReason): string {
    switch (reason) {
      case 'HAS_BALANCE':
        return 'Your PKCash balance must be below $1 to claim free PKCash';
      case 'COOLDOWN_ACTIVE':
        return 'You can only claim free PKCash once every 24 hours';
      case 'NOT_VERIFIED':
        return 'Please verify your email address first';
      case 'AGE_NOT_VERIFIED':
        return 'You must be 18+ and have your age verified';
      case 'GEO_RESTRICTED':
        return 'Free PKCash is not available in your location';
      case 'VPN_DETECTED':
        return 'Please disable your VPN to claim free PKCash';
      case 'ACCOUNT_SUSPENDED':
        return 'Your account is not eligible for free PKCash';
      case 'DAILY_LIMIT_REACHED':
        return 'Daily limit for free PKCash has been reached. Try again tomorrow';
      default:
        return 'Unable to claim free PKCash at this time';
    }
  }

  // ============================================================
  // KYC VERIFICATION
  // ============================================================

  /**
   * Get user's KYC status
   */
  async getKYCStatus(userId: string): Promise<KYCVerification | null> {
    try {
      const kycRef = doc(db, 'kycVerifications', userId);
      const kycSnap = await getDoc(kycRef);
      
      if (!kycSnap.exists()) {
        return null;
      }

      const data = kycSnap.data();
      return {
        ...data,
        verifiedAt: data.verifiedAt?.toDate?.(),
        rejectedAt: data.rejectedAt?.toDate?.(),
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
      } as KYCVerification;
    } catch (error) {
      console.error('[ComplianceService] Error getting KYC status:', error);
      return null;
    }
  }

  /**
   * Update KYC status (called by webhook or admin)
   */
  async updateKYCStatus(
    userId: string,
    status: KYCStatus,
    details?: Partial<KYCVerification>
  ): Promise<void> {
    try {
      const kycRef = doc(db, 'kycVerifications', userId);
      
      await setDoc(kycRef, {
        userId,
        status,
        ...details,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // Also update user profile
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        kycStatus: status,
      });

      // Audit log
      await this.logAudit(
        status === 'verified' ? 'KYC_VERIFIED' : 
        status === 'rejected' ? 'KYC_REJECTED' : 'KYC_STARTED',
        userId,
        undefined,
        { status, ...details }
      );
    } catch (error) {
      console.error('[ComplianceService] Error updating KYC status:', error);
      throw error;
    }
  }

  /**
   * Check if user needs KYC for an action
   */
  async requiresKYC(userId: string, action: 'redemption' | 'pkcash_play' | 'deposit'): Promise<boolean> {
    const settings = await this.getPlatformSettings();
    
    switch (action) {
      case 'redemption':
        if (!settings.kycRequiredForRedemption) return false;
        break;
      case 'pkcash_play':
        if (!settings.kycRequiredForPKCashPlay) return false;
        break;
      case 'deposit':
        if (!settings.kycRequiredForDeposit) return false;
        break;
    }

    const kycStatus = await this.getKYCStatus(userId);
    return !kycStatus || kycStatus.status !== 'verified';
  }

  // ============================================================
  // REDEMPTION ELIGIBILITY
  // ============================================================

  /**
   * Check if user can request a redemption
   */
  async checkRedemptionEligibility(
    userId: string, 
    amount: number
  ): Promise<RedemptionEligibility> {
    try {
      const settings = await this.getPlatformSettings();
      const [wallet, user, kycStatus, geoCheck] = await Promise.all([
        this.getUserWallet(userId),
        this.getUserProfile(userId),
        this.getKYCStatus(userId),
        geoService.checkIP(),
      ]);

      // Get recent redemptions for daily/weekly limits
      const [dailyTotal, weeklyTotal] = await Promise.all([
        this.getRedemptionTotal(userId, 'day'),
        this.getRedemptionTotal(userId, 'week'),
      ]);

      // Calculate account age
      const accountAge = user?.createdAt 
        ? Math.floor((Date.now() - user.createdAt.getTime()) / (1000 * 60 * 60 * 24))
        : 0;

      const checks = {
        minAmount: {
          passed: amount >= settings.minRedemption,
          required: settings.minRedemption,
          actual: amount,
        },
        maxDaily: {
          passed: dailyTotal + amount <= settings.maxRedemptionDaily,
          limit: settings.maxRedemptionDaily,
          used: dailyTotal,
        },
        maxWeekly: {
          passed: weeklyTotal + amount <= settings.maxRedemptionWeekly,
          limit: settings.maxRedemptionWeekly,
          used: weeklyTotal,
        },
        playthrough: {
          passed: (wallet?.pkCashPlaythrough || 0) <= 0,
          required: wallet?.pkCashPlaythrough || 0,
          actual: 0,
        },
        kycStatus: {
          passed: !settings.kycRequiredForRedemption || kycStatus?.status === 'verified',
          status: (kycStatus?.status || 'none') as KYCStatus,
        },
        geoLocation: {
          passed: geoCheck.allowed,
          location: geoService.formatLocation(geoCheck),
        },
        accountAge: {
          passed: accountAge >= 1, // At least 1 day old
          days: accountAge,
        },
      };

      // Determine overall eligibility
      const allPassed = Object.values(checks).every(check => check.passed);
      
      let reason: string | undefined;
      if (!checks.minAmount.passed) {
        reason = `Minimum redemption is $${settings.minRedemption}`;
      } else if (!checks.maxDaily.passed) {
        reason = `Daily limit of $${settings.maxRedemptionDaily} exceeded`;
      } else if (!checks.maxWeekly.passed) {
        reason = `Weekly limit of $${settings.maxRedemptionWeekly} exceeded`;
      } else if (!checks.playthrough.passed) {
        reason = `Playthrough requirement not met ($${checks.playthrough.required.toFixed(2)} remaining)`;
      } else if (!checks.kycStatus.passed) {
        reason = 'Identity verification required';
      } else if (!checks.geoLocation.passed) {
        reason = 'Redemption not available in your location';
      } else if (!checks.accountAge.passed) {
        reason = 'Account must be at least 1 day old';
      }

      return {
        eligible: allPassed,
        reason,
        checks,
      };
    } catch (error) {
      console.error('[ComplianceService] Error checking redemption eligibility:', error);
      return {
        eligible: false,
        reason: 'Unable to verify eligibility',
        checks: {} as any,
      };
    }
  }

  /**
   * Get total redemption amount for period
   */
  private async getRedemptionTotal(
    userId: string, 
    period: 'day' | 'week' | 'month'
  ): Promise<number> {
    try {
      const now = new Date();
      let startDate: Date;
      
      switch (period) {
        case 'day':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          const dayOfWeek = now.getDay();
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
      }

      const redemptionsRef = collection(db, 'redemptions');
      const q = query(
        redemptionsRef,
        where('userId', '==', userId),
        where('requestedAt', '>=', Timestamp.fromDate(startDate)),
        where('status', 'in', ['pending_kyc', 'pending_review', 'approved', 'processing', 'completed'])
      );

      const snapshot = await getDocs(q);
      let total = 0;
      
      snapshot.forEach(doc => {
        total += doc.data().amount || 0;
      });

      return total;
    } catch (error) {
      console.error('[ComplianceService] Error getting redemption total:', error);
      return 0;
    }
  }

  // ============================================================
  // FULL COMPLIANCE CHECK
  // ============================================================

  /**
   * Perform full compliance check for a user action
   */
  async performComplianceCheck(
    userId: string,
    action: 'pkcash_play' | 'redemption' | 'deposit' | 'amoe'
  ): Promise<ComplianceCheckResult> {
    try {
      const [settings, user, geoCheck] = await Promise.all([
        this.getPlatformSettings(),
        this.getUserProfile(userId),
        geoService.checkIP(),
      ]);

      const checks = {
        geoCheck: { passed: true, reason: undefined as string | undefined },
        ageCheck: { passed: true, reason: undefined as string | undefined },
        kycCheck: { passed: true, reason: undefined as string | undefined },
        vpnCheck: { passed: true, reason: undefined as string | undefined },
      };

      // Geo check
      if (!geoCheck.allowed) {
        checks.geoCheck = {
          passed: false,
          reason: geoService.getBlockReasonText(geoCheck.reason!),
        };
      }

      // VPN check
      if (settings.vpnBlockingEnabled && (geoCheck.isVPN || geoCheck.isProxy)) {
        checks.vpnCheck = {
          passed: false,
          reason: 'VPN or proxy detected',
        };
      }

      // Age check
      if (user?.dateOfBirth) {
        const age = this.calculateAge(new Date(user.dateOfBirth));
        if (age < settings.minimumAge) {
          checks.ageCheck = {
            passed: false,
            reason: `Must be ${settings.minimumAge}+ years old`,
          };
        }
      } else if (action !== 'amoe') {
        // Age required for most actions
        checks.ageCheck = {
          passed: false,
          reason: 'Age verification required',
        };
      }

      // KYC check (for redemption)
      if (action === 'redemption' && settings.kycRequiredForRedemption) {
        const kyc = await this.getKYCStatus(userId);
        if (!kyc || kyc.status !== 'verified') {
          checks.kycCheck = {
            passed: false,
            reason: 'Identity verification required',
          };
        }
      }

      // Determine overall result
      const allPassed = Object.values(checks).every(c => c.passed);
      const failedCheck = Object.entries(checks).find(([_, c]) => !c.passed);

      return {
        allowed: allPassed,
        checks,
        overallReason: failedCheck?.[1].reason,
      };
    } catch (error) {
      console.error('[ComplianceService] Error in compliance check:', error);
      return {
        allowed: false,
        checks: {
          geoCheck: { passed: false, reason: 'Check failed' },
          ageCheck: { passed: false, reason: 'Check failed' },
          kycCheck: { passed: false, reason: 'Check failed' },
          vpnCheck: { passed: false, reason: 'Check failed' },
        },
        overallReason: 'Compliance check failed',
      };
    }
  }

  // ============================================================
  // HELPER METHODS
  // ============================================================

  /**
   * Calculate age from date of birth
   */
  private calculateAge(dob: Date): number {
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    
    return age;
  }

  /**
   * Get user's wallet
   */
  private async getUserWallet(userId: string): Promise<any> {
    try {
      const walletRef = doc(db, 'wallets', userId);
      const walletSnap = await getDoc(walletRef);
      return walletSnap.exists() ? walletSnap.data() : null;
    } catch (error) {
      console.error('[ComplianceService] Error getting wallet:', error);
      return null;
    }
  }

  /**
   * Get user profile
   */
  private async getUserProfile(userId: string): Promise<any> {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) return null;
      
      const data = userSnap.data();
      return {
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
      };
    } catch (error) {
      console.error('[ComplianceService] Error getting user profile:', error);
      return null;
    }
  }

  /**
   * Log audit event
   */
  async logAudit(
    action: AuditAction,
    userId: string,
    adminId?: string,
    details?: Record<string, any>
  ): Promise<void> {
    try {
      const logsRef = collection(db, 'auditLogs');
      await addDoc(logsRef, {
        action,
        userId,
        adminId,
        details,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      console.error('[ComplianceService] Error logging audit:', error);
    }
  }
}

// Export singleton instance
export const complianceService = new ComplianceService();
export default complianceService;